import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Button, Avatar, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../config';

const IDLE_MS = 45 * 60 * 1000;
const EVENTS  = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export default function SessionLock() {
  // Initialise from localStorage so reload preserves the locked state
  const [locked,   setLocked]   = useState(() => localStorage.getItem('session_locked') === '1');
  const [password, setPassword] = useState('');
  const [loading,  setLoading]  = useState(false);
  const timerRef = useRef(null);

  const firstName = localStorage.getItem('firstName') || 'Staff';
  const email     = localStorage.getItem('email')     || '';

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      localStorage.setItem('session_locked', '1');
      setLocked(true);
    }, IDLE_MS);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) return;
    resetTimer();
    EVENTS.forEach(ev => document.addEventListener(ev, resetTimer, true));
    return () => {
      clearTimeout(timerRef.current);
      EVENTS.forEach(ev => document.removeEventListener(ev, resetTimer, true));
    };
  }, [resetTimer]);

  const handleCheckIn = async () => {
    if (!password.trim()) return;

    // No email stored (OTP users) — can't re-auth, force full sign-in
    if (!email.trim()) {
      localStorage.clear();
      window.location.href = '/sign-in';
      return;
    }

    setLoading(true);
    try {
      // Uses a dedicated verify-password endpoint — no OTP sent, just checks credentials
      const res = await axios.post(`${baseUrl}/auth/verify-password`, {
        identifier: email,  // email stored at login (identifier = email or phone)
        password,
      });
      if (res.data?.status) {
        localStorage.removeItem('session_locked');
        setLocked(false);
        setPassword('');
        resetTimer();
        message.success('Checked in successfully!');
      } else {
        message.error(res.data?.message || 'Incorrect password');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        message.error('Incorrect password. Please try again.');
      } else if (status === 429) {
        message.error('Too many attempts. Please wait 15 minutes.');
      } else {
        message.error('Server error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!locked) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(10, 20, 40, 0.92)',
      backdropFilter: 'blur(10px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '48px 52px',
        textAlign: 'center', width: 380,
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        animation: 'fadeIn 0.25s ease',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'linear-gradient(135deg,#7a0000,#2c4a6e)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <LockOutlined style={{ fontSize: 28, color: '#fff' }} />
        </div>

        <h2 style={{ color: '#7a0000', marginBottom: 6, fontWeight: 800 }}>Checked Out</h2>
        <p style={{ color: '#64748b', fontSize: 13, marginBottom: 28 }}>
          Inactive for 45 minutes. Enter your password to check in.
        </p>

        <Avatar size={60} style={{ background: '#1890ff', fontSize: 22, fontWeight: 700, marginBottom: 10 }}>
          {firstName.charAt(0).toUpperCase()}
        </Avatar>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#7a0000', marginBottom: 24 }}>
          {firstName}
        </div>

        {email.trim() ? (
          <>
            <Input.Password
              placeholder="Enter your password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onPressEnter={handleCheckIn}
              size="large"
              style={{ marginBottom: 14, borderRadius: 8 }}
              autoFocus
            />
            <Button
              type="primary" size="large" block loading={loading}
              onClick={handleCheckIn}
              disabled={!password.trim()}
              style={{ borderRadius: 8, height: 44, fontWeight: 700, fontSize: 15 }}
            >
              Check In
            </Button>
          </>
        ) : (
          // OTP-login users have no email stored — redirect to full sign-in
          <Button
            type="primary" size="large" block
            onClick={() => { localStorage.removeItem('session_locked'); localStorage.clear(); window.location.href = '/sign-in'; }}
            style={{ borderRadius: 8, height: 44, fontWeight: 700, fontSize: 15 }}
          >
            Sign In Again
          </Button>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
      `}</style>
    </div>
  );
}
